import { addDays, format, parse, startOfWeek } from "date-fns";

import { type CalendarDay, dayMarker, pickedDay } from "@/lib/clinic-clock";
import { formatDate, formatDateTime, formatTime } from "@/lib/format-data";
import {
  compareValuesAscending,
  isNumericDataType,
} from "@/lib/measurement-utils";
import { getTimezone } from "@/lib/timezone";
import { zonedDateKey } from "@/lib/timezone-dates";

import type {
  MeasurementField,
  MeasurementValueOutcome,
  MeasurementValueSummary,
  ReferenceRange,
} from "@/types/measurement";

/** Time window a measurement row stands for. */
export type MeasurementBucketGranularity = "NONE" | "DAY" | "WEEK";

/** How multiple readings inside one bucket collapse into one row. */
export type MeasurementAggregationRule = "LATEST" | "SUM" | "AVERAGE";

export const MEASUREMENT_BUCKET_LABELS: Record<
  MeasurementBucketGranularity,
  string
> = {
  NONE: "No grouping",
  DAY: "Day",
  WEEK: "Week",
};

export const MEASUREMENT_RULE_LABELS: Record<
  MeasurementAggregationRule,
  string
> = {
  LATEST: "Latest",
  SUM: "Sum",
  AVERAGE: "Average",
};

/** Short badge text for a synthesised row. */
export const MEASUREMENT_RULE_HINTS: Record<
  MeasurementAggregationRule,
  string
> = {
  LATEST: "latest",
  SUM: "sum",
  AVERAGE: "avg",
};

/** Bucket key used for values with no measurement date, matching `groupValuesByDate`. */
export const UNKNOWN_BUCKET_KEY = "unknown";

/** Hoisted: `bucketKeyToDate` runs once per row per render. */
const DATE_ONLY_KEY = /^\d{4}-\d{2}-\d{2}$/;

export interface AggregatedMeasurementValue extends MeasurementValueSummary {
  /** Present only when the row is synthesised from >1 reading. */
  aggregation?: { rule: MeasurementAggregationRule; count: number } | null;
  /**
   * Set when >1 device brand reported this field in this bucket. The row is then
   * one brand's real reading rather than a figure combined across brands, and the
   * badge says so — a patient wearing two devices must not be shown a total no
   * device recorded, nor one brand silently standing in for both.
   */
  fromMultipleDevices?: boolean;
}

/**
 * The slice of a field that aggregation actually reads. Kept narrow so callers
 * holding only part of a field (the trend chart) can synthesise a map.
 */
export type AggregationFieldInfo = Pick<
  MeasurementField,
  "dataType" | "decimalPlaces" | "referenceRanges"
>;

/**
 * The bucket a value belongs to. Always sortable via `new Date(key)` so
 * `sortedDates()` orders buckets without knowing the granularity.
 */
export function bucketKeyFor(
  value: MeasurementValueSummary,
  granularity: MeasurementBucketGranularity,
  timeZone?: string,
): string {
  if (granularity === "NONE") {
    return value.measurementDate || value.createdDate || value.id;
  }
  if (!value.measurementDate) return UNKNOWN_BUCKET_KEY;
  const day = zonedDateKey(
    value.measurementDate,
    timeZone ?? getTimezone(),
  ) as CalendarDay;
  if (granularity === "WEEK") {
    return pickedDay(startOfWeek(dayMarker(day), { weekStartsOn: 0 }));
  }
  return day;
}

/**
 * Group values into time buckets. At `DAY` this is `groupValuesByDate`.
 */
export function groupValuesByBucket(
  values: MeasurementValueSummary[],
  granularity: MeasurementBucketGranularity,
  timeZone?: string,
): Record<string, MeasurementValueSummary[]> {
  const byBucket: Record<string, MeasurementValueSummary[]> = {};
  for (const value of values) {
    const key = bucketKeyFor(value, granularity, timeZone);
    if (!byBucket[key]) byBucket[key] = [];
    byBucket[key].push(value);
  }
  return byBucket;
}

/**
 * Client mirror of `evaluate_reference_range` in
 * `decoda/services/measurement/compute.py`, including its half-open [min, max)
 * intervals, so a synthesised value still gets a severity badge.
 */
export function evaluateReferenceRange(
  value: number,
  ranges: ReferenceRange[] | null | undefined,
): MeasurementValueOutcome | null {
  if (!ranges?.length) return null;
  for (const range of ranges) {
    if (range.min != null && value < range.min) continue;
    if (range.max != null && value >= range.max) continue;
    return {
      label: range.label ?? "",
      severity: range.severity ?? "NORMAL",
      color: range.color,
    };
  }
  return null;
}

function combineFieldValues(
  bucketKey: string,
  fieldId: string,
  ascending: MeasurementValueSummary[],
  field: AggregationFieldInfo | undefined,
  rule: MeasurementAggregationRule,
): AggregatedMeasurementValue {
  const latest = ascending[ascending.length - 1];

  // Whether more than one device brand reported here, worked out before the
  // LATEST early return because the marker has to ride that path too: at LATEST
  // the number IS a real reading, but it is one brand's reading and the other
  // brand is invisible unless the badge says so. Only non-null providers count —
  // a reading written before values carried a provider proves nothing about a
  // second device, and the projection now adopts those rows anyway.
  let onlyProvider: string | null = null;
  let fromMultipleDevices = false;
  for (const value of ascending) {
    const provider = value.wearableProvider;
    if (!provider) continue;
    if (onlyProvider === null) {
      onlyProvider = provider;
    } else if (onlyProvider !== provider) {
      fromMultipleDevices = true;
      break;
    }
  }
  const realReading: AggregatedMeasurementValue = fromMultipleDevices
    ? { ...latest, fromMultipleDevices: true }
    : latest;

  // Sum/average of a text, array or date field is meaningless, and a single
  // numeric reading has nothing to combine — hand back the real reading so its
  // id, outcome, goal, edit affordance and lab badge all keep working.
  if (rule === "LATEST") return realReading;
  if (!field || !isNumericDataType(field.dataType)) return realReading;

  // One pass: the sum, the contributing count and both source flags together.
  let total = 0;
  let count = 0;
  let everyReadingUserReported = true;
  let anyReadingLabSourced = false;
  let anyReadingInbodySourced = false;
  let anyReadingWearableSourced = false;
  for (const value of ascending) {
    if (value.numericValue == null) continue;
    total += value.numericValue;
    count += 1;
    if (!value.isUserReported) everyReadingUserReported = false;
    if (value.isLabSourced) anyReadingLabSourced = true;
    if (value.isInbodySourced) anyReadingInbodySourced = true;
    if (value.isWearableSourced) anyReadingWearableSourced = true;
  }
  if (count < 2) return realReading;

  // Two brands in one bucket: combining them invents a number no device recorded
  // — 8,412 OURA steps plus 10,930 GARMIN steps is not 19,342 of anything, and
  // nothing on screen would call it a total. Hand back the latest real reading
  // and let the badge say where it came from. Drawing a series per brand is the
  // real answer and is deliberately a follow-up; the projection keeps both rows
  // so that lands without a migration.
  if (fromMultipleDevices) return realReading;

  const raw = rule === "SUM" ? total : total / count;
  const decimalPlaces = field.decimalPlaces;
  const combined =
    decimalPlaces != null && decimalPlaces >= 0
      ? Number(raw.toFixed(decimalPlaces))
      : raw;

  return {
    ...latest,
    id: `agg-${rule}-${bucketKey}-${fieldId}`,
    numericValue: combined,
    textValue: null,
    arrayValue: null,
    outcome: evaluateReferenceRange(combined, field.referenceRanges),
    // Server-computed against raw history, so wrong for a synthetic row. Table
    // and Cards derive row-to-row deltas from adjacent buckets themselves.
    totalChange: null,
    recentChange: null,
    trendDirection: null,
    // Describes how ONE contributing reading was computed, so its "uses prior
    // value" badge would be attached to a number no reading actually has.
    computedDetail: null,
    isUserReported: everyReadingUserReported,
    isLabSourced: anyReadingLabSourced,
    isInbodySourced: anyReadingInbodySourced,
    isWearableSourced: anyReadingWearableSourced,
    aggregation: { rule, count },
  };
}

/**
 * Collapse each bucket to one row per field. At `LATEST` this is
 * `deduplicateValuesByDate`.
 */
export function aggregateBucketValues(
  valuesByBucket: Record<string, MeasurementValueSummary[]>,
  fieldMap: ReadonlyMap<string, AggregationFieldInfo>,
  rule: MeasurementAggregationRule,
): Record<string, AggregatedMeasurementValue[]> {
  const result: Record<string, AggregatedMeasurementValue[]> = {};

  for (const [bucketKey, values] of Object.entries(valuesByBucket)) {
    const byField = new Map<string, MeasurementValueSummary[]>();
    for (const value of [...values].sort(compareValuesAscending)) {
      const existing = byField.get(value.fieldId);
      if (existing) existing.push(value);
      else byField.set(value.fieldId, [value]);
    }

    result[bucketKey] = Array.from(byField.entries()).map(
      ([fieldId, fieldValues]) =>
        combineFieldValues(
          bucketKey,
          fieldId,
          fieldValues,
          fieldMap.get(fieldId),
          rule,
        ),
    );
  }

  return result;
}

/**
 * Turn a bucket key back into a Date, or null when it carries no date.
 */
export function bucketKeyToDate(key: string): Date | null {
  if (!key || key === UNKNOWN_BUCKET_KEY) return null;
  const date = DATE_ONLY_KEY.test(key)
    ? parse(key, "yyyy-MM-dd", new Date())
    : new Date(key);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Human label for a bucket. `long` reads in a table cell, `short` on a chart axis.
 * `timeZone` must be the zone the values were bucketed on, so a NONE key reads
 * on the same clock that grouped it.
 */
export function formatBucketLabel(
  key: string,
  granularity: MeasurementBucketGranularity,
  timeZone: string,
  style: "long" | "short" = "long",
): string {
  const date = bucketKeyToDate(key);
  if (!date) return key;

  // A NONE key is the reading's own timestamp, so it reads on the clinic's
  // clock; DAY and WEEK keys are already its days.
  if (granularity === "NONE") {
    return style === "short"
      ? `${formatDate(key, { month: "short", day: "numeric", timeZone })} ${formatTime(key, true, timeZone)}`
      : formatDateTime(key, true, true, undefined, timeZone);
  }

  if (granularity === "WEEK") {
    if (style === "short") return format(date, "MMM d");
    return `${format(date, "MMM d")} – ${format(addDays(date, 6), "MMM d, yyyy")}`;
  }

  return style === "short"
    ? format(date, "MMM d")
    : format(date, "MMM d, yyyy");
}

/**
 * The `aggregation` marker on a value, or null when it is a real reading.
 */
export function aggregationOf(
  value: MeasurementValueSummary,
): AggregatedMeasurementValue["aggregation"] {
  return (value as AggregatedMeasurementValue).aggregation ?? null;
}
